import type HighchartsType from 'highcharts/highstock';

import { fullScreenChartPalette } from '@/config/constants';

import type {
  CreateFullScreenStockOptionsParams,
  FullScreenLegendSeries
} from './fullScreenChartTypes';

import type { ExtendedSeriesOptionsType } from '@/features/_shared/chart/types/highcharts';
import { clock } from '@/services/clock';

type StockSeriesOptions = NonNullable<HighchartsType.Options['series']>[number];

// ── Series identity helpers ────────────────────────────────────────────────

export const getSeriesId = (series: ExtendedSeriesOptionsType, index: number): string =>
  String(series.id ?? series.name ?? `series-${index + 1}`);

const getSeriesName = (series: ExtendedSeriesOptionsType, index: number): string => {
  if (typeof series.name === 'string' && series.name.length > 0) return series.name;
  if (typeof series.custom.Title === 'string' && series.custom.Title.length > 0) {
    return series.custom.Title;
  }
  return getSeriesId(series, index);
};

const getSeriesColor = (series: ExtendedSeriesOptionsType, index: number): string => {
  const paletteIndex =
    typeof series.custom.combinedIndex === 'number' ? series.custom.combinedIndex : index;
  return (
    series.custom.color ??
    fullScreenChartPalette[Math.abs(paletteIndex) % fullScreenChartPalette.length]
  );
};

// ── Point helpers ──────────────────────────────────────────────────────────

const clonePoint = (point: unknown): unknown => {
  if (Array.isArray(point)) return [...point];
  if (!point || typeof point !== 'object') return point;
  const custom = 'custom' in point ? point.custom : undefined;
  return { ...point, custom: custom && typeof custom === 'object' ? { ...custom } : custom };
};

const getPointX = (point: unknown): number | undefined => {
  if (Array.isArray(point)) return typeof point[0] === 'number' ? point[0] : undefined;
  if (!point || typeof point !== 'object' || !('x' in point)) return undefined;
  return typeof point.x === 'number' ? point.x : undefined;
};

export const getInitialRangeButtonIndex = (chartSeries: ExtendedSeriesOptionsType[]): number => {
  const oneYearAgo = clock.date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  const hasMoreThanOneYear = chartSeries.some(series => {
    const firstPoint = series.data.find(point => getPointX(point) !== undefined);
    const firstX = getPointX(firstPoint);
    return firstX !== undefined && firstX < oneYearAgo.getTime();
  });

  return hasMoreThanOneYear ? 3 : 1;
};

export const toHighchartsSeries = (
  series: ExtendedSeriesOptionsType,
  index: number,
  hiddenSeriesIds: string[]
): HighchartsType.SeriesOptionsType => {
  const id = getSeriesId(series, index);
  return {
    ...series,
    id,
    name: getSeriesName(series, index),
    color: getSeriesColor(series, index),
    custom: { ...series.custom },
    data: series.data.map(clonePoint),
    showInNavigator: true,
    type: series.type,
    visible: !hiddenSeriesIds.includes(id)
  } as HighchartsType.SeriesOptionsType;
};

// ── Legend ─────────────────────────────────────────────────────────────────

export const createFullScreenLegendSeries = ({
  chartSeries,
  hiddenSeriesIds
}: CreateFullScreenStockOptionsParams): FullScreenLegendSeries[] =>
  chartSeries.map((series, index) => {
    const id = getSeriesId(series, index);
    const context = series.custom;
    return {
      alias: context.Alias,
      code: context.Code,
      color: getSeriesColor(series, index),
      currency: context.Currency,
      id,
      name: getSeriesName(series, index),
      period: context.Period,
      periodType: context.PeriodType,
      timeRef: context.TimeRef,
      units: context.Units,
      visible: !hiddenSeriesIds.includes(id)
    };
  });

// ── Linked series preservation ─────────────────────────────────────────────

const getLinkedTo = (seriesOptions: HighchartsType.SeriesOptionsType): string | undefined => {
  const { linkedTo } = seriesOptions as { linkedTo?: unknown };
  return typeof linkedTo === 'string' ? linkedTo : undefined;
};

const getRuntimeSeriesId = (series: HighchartsType.Series): string | undefined => {
  if (typeof series.options.id === 'string') return series.options.id;
  if (typeof series.userOptions.id === 'string') return series.userOptions.id;
  return undefined;
};

const getSeriesOptionsId = (seriesOptions: StockSeriesOptions): string | undefined => {
  const { id } = seriesOptions as { id?: unknown };
  return typeof id === 'string' ? id : undefined;
};

const getRuntimeLinkedSeriesOptions = (
  chart: HighchartsType.Chart,
  baseSeriesIds: Set<string>
): StockSeriesOptions[] =>
  chart.series.flatMap(series => {
    const linkedTo = getLinkedTo(series.userOptions) ?? getLinkedTo(series.options);
    const seriesId = getRuntimeSeriesId(series);
    if (!linkedTo || !baseSeriesIds.has(linkedTo) || (seriesId && baseSeriesIds.has(seriesId))) {
      return [];
    }
    return [{ ...series.userOptions, visible: series.visible } as StockSeriesOptions];
  });

export const preserveRuntimeLinkedSeries = (
  options: HighchartsType.Options,
  chart: HighchartsType.Chart
): HighchartsType.Options => {
  const baseSeries = options.series ?? [];
  const baseSeriesIds = new Set(
    baseSeries.map(series => getSeriesOptionsId(series)).filter((id): id is string => Boolean(id))
  );
  const runtimeLinkedSeries = getRuntimeLinkedSeriesOptions(chart, baseSeriesIds);
  if (runtimeLinkedSeries.length === 0) return options;
  return { ...options, series: [...baseSeries, ...runtimeLinkedSeries] };
};
